<template>
    <div class="tab-detail">
        <div class="header">
            <div class="title">{{ $t("googlePlay.apps") }}</div>
            <div class="header-actions">
                <a-button type="primary" @click="showAddModal = true">
                    <span class="material-icons" style="font-size: 16px; margin-right: 4px;">add</span>
                    {{ $t("googlePlay.addApp") }}
                </a-button>
                <a-button @click="fetchApps" :loading="loading">
                    <a-icon type="reload" /> {{ $t("googlePlay.refresh") }}
                </a-button>
            </div>
        </div>
        <div class="apps-container gpr-app-list" v-if="!loading">
            <article class="gpr-app-list__card" v-for="app in apps" :key="app._id">
                <div class="gpr-app-list__main">
                    <div class="gpr-app-list__icon">
                        <img v-if="app.iconUrl" :src="app.iconUrl" alt="" />
                        <span class="material-icons" v-else>android</span>
                    </div>
                    <div class="gpr-app-list__info">
                        <h3 class="gpr-app-list__name">{{ app.appName }}</h3>
                        <div class="gpr-app-list__package">{{ app.packageName }}</div>
                        <div class="gpr-app-list__meta">
                            <span v-if="app.totalReviewCount" class="gpr-app-list__badge">
                                <span class="material-icons">reviews</span>
                                <span>{{ app.totalReviewCount }} reviews</span>
                            </span>
                            <span v-if="app.lastSyncedAt" class="gpr-app-list__badge">
                                <span class="material-icons">sync</span>
                                <span>{{ formatDate(app.lastSyncedAt) }}</span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="gpr-app-list__actions">
                    <a-button size="small" type="primary" @click="syncReviews(app)" :loading="syncingApp === app._id">
                        <a-icon type="sync" /> {{ $t("googlePlay.syncReviews") }}
                    </a-button>
                    <a-button size="small" @click="confirmRemove(app)" class="gpr-app-list__btn-remove">
                        <a-icon type="delete" />
                    </a-button>
                </div>
            </article>
        </div>
        <div class="loading" v-else>
            <a-spin />
        </div>
        <div class="empty" v-if="!loading && apps.length === 0">
            <a-empty :description="$t('googlePlay.noAppsFound')">
                <a-button type="primary" @click="showAddModal = true">
                    {{ $t("googlePlay.addApp") }}
                </a-button>
            </a-empty>
        </div>

        <!-- Add App Modal -->
        <a-modal
            v-model="showAddModal"
            :title="$t('googlePlay.addApp')"
            @ok="addApp"
            :confirm-loading="adding"
        >
            <a-form-item :label="$t('googlePlay.packageName')" :required="true">
                <a-input
                    v-model="newApp.packageName"
                    placeholder="com.example.myapp"
                />
            </a-form-item>
            <a-form-item :label="$t('googlePlay.appName')" :required="true">
                <a-input
                    v-model="newApp.appName"
                    :placeholder="$t('googlePlay.appNamePlaceholder')"
                />
            </a-form-item>
            <a-form-item :label="$t('googlePlay.iconUrl')">
                <a-input
                    v-model="newApp.iconUrl"
                    placeholder="https://..."
                />
            </a-form-item>
        </a-modal>

        <!-- Remove Confirmation Modal -->
        <a-modal
            v-model="removeModal.visible"
            :title="$t('googlePlay.removeApp')"
            @ok="removeApp"
            :ok-type="'danger'"
        >
            <p>{{ $t("googlePlay.removeAppConfirm", { name: removeModal.app ? removeModal.app.appName : '' }) }}</p>
        </a-modal>
    </div>
</template>

<script>
import { GOOGLE_PLAY_API } from "../../../../../api/google-play";
import moment from "moment";

export default {
    name: "AppList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            apps: [],
            loading: false,
            showAddModal: false,
            adding: false,
            syncingApp: null,
            newApp: {
                packageName: "",
                appName: "",
                iconUrl: "",
            },
            removeModal: {
                visible: false,
                app: null,
            },
        };
    },
    methods: {
        async fetchApps() {
            this.loading = true;
            try {
                const response = await GOOGLE_PLAY_API.listApps(this.channel._id);
                this.apps = response.data;
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorFetchingApps"));
            } finally {
                this.loading = false;
            }
        },
        async addApp() {
            if (!this.newApp.packageName || !this.newApp.appName) {
                this.$message.warning(this.$t("googlePlay.fillRequiredFields"));
                return;
            }
            this.adding = true;
            try {
                await GOOGLE_PLAY_API.addApp(this.channel._id, this.newApp);
                this.$message.success(this.$t("googlePlay.appAdded"));
                this.showAddModal = false;
                this.newApp = { packageName: "", appName: "", iconUrl: "" };
                await this.fetchApps();
            } catch (err) {
                var errorMessage = err.response && err.response.data && err.response.data.message;
                this.$message.error(errorMessage || this.$t("googlePlay.errorAddingApp"));
            } finally {
                this.adding = false;
            }
        },
        async syncReviews(app) {
            this.syncingApp = app._id;
            try {
                await GOOGLE_PLAY_API.fetchReviews(this.channel._id, app.packageName);
                this.$message.success(this.$t("googlePlay.reviewsSynced"));
                await this.fetchApps();
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorSyncingReviews"));
            } finally {
                this.syncingApp = null;
            }
        },
        confirmRemove(app) {
            this.removeModal.app = app;
            this.removeModal.visible = true;
        },
        async removeApp() {
            try {
                await GOOGLE_PLAY_API.removeApp(this.removeModal.app._id);
                this.$message.success(this.$t("googlePlay.appRemoved"));
                this.removeModal.visible = false;
                this.removeModal.app = null;
                await this.fetchApps();
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorRemovingApp"));
            }
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
    },
    mounted() {
        this.fetchApps();
    },
};
</script>

<style lang="scss" scoped>
.tab-detail {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .header-actions {
            display: flex;
            gap: 10px;
        }
    }

    .apps-container {
        margin-top: 15px;
    }

    /* İzole uygulama listesi: dışarıdan css yemez, dışarıyı etkilemez */
    .gpr-app-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 380px), 1fr));
        gap: 24px;

        &__card {
            display: flex;
            flex-direction: column;
            gap: 20px;
            padding: 24px;
            margin: 0;
            background: var(--color-bg-subtle, #fafafa);
            border: 1px solid var(--color-border-default, #e5e7eb);
            border-radius: 16px;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
            transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.2s ease;

            &:hover {
                box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.08);
                border-color: rgba(52, 168, 83, 0.4);
            }
        }

        &__main {
            display: flex;
            align-items: flex-start;
            gap: 18px;
            min-width: 0;
        }

        &__icon {
            width: 56px;
            height: 56px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(145deg, #34a853 0%, #1e8e3e 50%, #0d8043 100%);
            border-radius: 14px;
            overflow: hidden;
            box-shadow: 0 2px 12px rgba(52, 168, 83, 0.3);

            img {
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            .material-icons {
                font-size: 28px;
                color: #fff;
            }
        }

        &__info {
            flex: 1;
            min-width: 0;
            overflow: hidden;
        }

        &__name {
            margin: 0 0 4px;
            padding: 0;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.3;
            letter-spacing: -0.01em;
            color: var(--color-text-strong-default, #111827);
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
        }

        &__package {
            margin: 0 0 12px;
            padding: 0;
            font-size: 11px;
            line-height: 1.4;
            font-family: ui-monospace, "SF Mono", monospace;
            color: var(--color-text-default, #6b7280);
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
        }

        &__meta {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin: 0;
            padding: 0;
            min-width: 0;
        }

        &__badge {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 6px 10px;
            background: rgba(52, 168, 83, 0.08);
            border-radius: 8px;
            font-size: 12px;
            font-weight: 500;
            line-height: 1.2;
            color: var(--color-text-default, #374151);
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;

            span:last-child {
                word-wrap: break-word;
                overflow-wrap: break-word;
                word-break: break-word;
            }

            .material-icons {
                font-size: 14px;
                flex-shrink: 0;
                color: #34a853;
            }
        }

        &__actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 10px;
            padding-top: 4px;
            border-top: 1px solid var(--color-border-default, #e5e7eb);
        }

        &__btn-remove {
            color: #dc2626;

            &:hover {
                color: #b91c1c;
                border-color: #dc2626;
            }
        }
    }

    .loading,
    .empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }
}
</style>
